import type { APIError } from "@k8s-dashboard/contracts";
import { readAuthSession } from "./session";
import {
  acknowledgementMatches,
  type NamespaceSelectionTransport,
} from "@/state/namespaceSelection";

export class HttpError extends Error {
  constructor(readonly status: number, readonly body: APIError) {
    super(body.message);
    this.name = "HttpError";
  }
}

export class NamespaceSelectionAcknowledgementError extends Error {
  constructor() {
    super("Namespace selection acknowledgement mismatch");
    this.name = "NamespaceSelectionAcknowledgementError";
  }
}

let csrfToken = "";
export type RefreshResult = "refreshed" | "expired" | "unavailable";
let refreshInFlight: Promise<RefreshResult> | undefined;

// manager 모드(Dhub2.0 인증 위임): AuthGate가 Dhub2.0 세션에서 받아온 access token을
// Bearer로 붙입니다. 갱신 함수도 AuthGate가 등록합니다 — 이 모듈은 보관·부착만 합니다.
let managerToken = "";
let managerRefresh: (() => Promise<boolean>) | undefined;

/* 인증값은 자격 증명이지 cache 신원이 아닙니다. token·CSRF·profile·role·Scope 값을
   노출하지 않고 이전 자격으로 가져온 데이터를 동기적으로 차단할 수 있도록,
   프로세스 로컬에서 단조 증가하는 generation만 보관합니다. */
let authGeneration = 0;
const authGenerationListeners = new Set<() => void>();

export function getAuthGeneration() {
	return authGeneration;
}

export function subscribeAuthGeneration(listener: () => void) {
	authGenerationListeners.add(listener);
	return () => authGenerationListeners.delete(listener);
}

export function notifyAuthIdentityTransition() {
	authGeneration += 1;
	for (const listener of authGenerationListeners) listener();
}

export function setManagerToken(value: string) {
	if (managerToken === value) return;
	managerToken = value;
	notifyAuthIdentityTransition();
	/* token 바이트는 비공개로 둡니다. 소비자는 API 검증 신원이 바뀌었을 수 있어
	   `/scope`를 다시 가져와야 한다는 사실만 압니다. */
	window.dispatchEvent(new Event("dashboard-auth-changed"));
}

export function setManagerTokenRefresher(fn?: () => Promise<boolean>) {
	managerRefresh = fn;
}

export function getManagerToken() {
	return managerToken;
}

// refreshAuth는 활성 인증 모드(manager Bearer 또는 세션 쿠키)에 맞는 갱신을 수행합니다.
// SSE처럼 두 모드를 구분하고 싶지 않은 호출자용입니다.
export async function refreshAuth(): Promise<RefreshResult> {
	if (managerRefresh) return (await managerRefresh().catch(() => false)) ? "refreshed" : "expired";
	return refreshSession();
}

export function setSessionCSRF(value: string) {
  csrfToken = value;
}

export function getSessionCSRF() {
	return csrfToken;
}

function isUnsafe(method: string) {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

function waitForRefresh(ms: number, signal: AbortSignal) {
	if (signal.aborted) return Promise.reject(signal.reason);
	return new Promise<void>((resolve, reject) => {
		const done = () => { signal.removeEventListener("abort", abort); resolve(); };
		const timer = window.setTimeout(done, ms);
		const abort = () => { window.clearTimeout(timer); signal.removeEventListener("abort", abort); reject(signal.reason); };
		signal.addEventListener("abort", abort, { once: true });
	});
}

export async function refreshSession(notify = true): Promise<RefreshResult> {
  if (!csrfToken) return "expired";
  const csrfBefore = csrfToken;
  refreshInFlight ??= (async (): Promise<RefreshResult> => {
	const controller = new AbortController(); const timeout = window.setTimeout(() => controller.abort(new DOMException("Authentication timeout", "TimeoutError")), 5_000);
	try {
		const res = await fetch("/api/v1/auth/refresh", { method: "POST", credentials: "same-origin", headers: { accept: "application/json", "X-CSRF-Token": csrfToken }, signal: controller.signal });
		if (res.status === 204) { const next=res.headers.get("X-CSRF-Token") ?? ""; if(!/^[A-Za-z0-9_-]{43}$/.test(next)) return "unavailable"; csrfToken=next; return "refreshed"; }
		if (res.status === 401) { await res.body?.cancel(); return "expired"; }
		if (res.status !== 409) { await res.body?.cancel(); return "unavailable"; }
		const retryAfter = Number(res.headers.get("Retry-After") ?? "0"); await res.body?.cancel();
		let wait = Math.min(2_000, Math.max(0, retryAfter * 1_000));
		for (let attempt = 0; attempt < 9; attempt++) {
			await waitForRefresh(wait, controller.signal); wait = 250;
			const session = await fetch("/api/v1/auth/session", { credentials: "same-origin", headers: { accept: "application/json" }, signal: controller.signal });
			if (!session.ok) { await session.body?.cancel(); return session.status === 401 ? "expired" : "unavailable"; }
			const value = await readAuthSession(session, controller.signal);
			if (value.authenticated) { if (value.csrfToken !== csrfBefore) { csrfToken = value.csrfToken; return "refreshed"; } continue; }
			if (!("refreshable" in value)) return "expired";
		}
		return "unavailable";
	} finally { window.clearTimeout(timeout); }
  })().catch(() => "unavailable" as const).finally(() => { refreshInFlight = undefined; });
  const result = await refreshInFlight;
  if (result === "refreshed" && notify) {
    notifyAuthIdentityTransition();
    window.dispatchEvent(new Event("dashboard-session-refreshed"));
  }
  if (result === "expired" && notify) {
    csrfToken = "";
    notifyAuthIdentityTransition();
    window.dispatchEvent(new Event("dashboard-session-expired"));
  }
  return result;
}

async function request(path: string, init: RequestInit, retry = true): Promise<Response> {
  const method = init.method ?? "GET";
  const headers = new Headers(init.headers);
  headers.set("accept", headers.get("accept") ?? "application/json");
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (isUnsafe(method) && csrfToken) headers.set("X-CSRF-Token", csrfToken);
  if (managerToken) headers.set("Authorization", `Bearer ${managerToken}`);
  const res = await fetch(new URL(path, window.location.origin), { ...init, headers, credentials: "same-origin" });
	if (res.status === 401 && retry && !path.startsWith("/api/v1/auth/")) {
		if (managerRefresh) {
			const ok = await managerRefresh().catch(() => false);
			if (ok) {
				if (init.signal?.aborted) throw init.signal.reason;
				return request(path, init, false);
			}
			return res;
		}
		const refresh = await refreshSession();
		if (refresh === "refreshed") {
			if (init.signal?.aborted) throw init.signal.reason;
			return request(path, init, false);
		}
		if (refresh === "unavailable") {
			throw new HttpError(503, { code: "auth_unavailable", message: "Authentication temporarily unavailable", requestId: "" });
		}
  }
  return res;
}

async function parseError(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as APIError | null;
  throw new HttpError(res.status, body ?? {
    code: res.status === 403 ? "forbidden" : "internal",
    message: `Request failed (${res.status})`,
    requestId: res.headers.get("X-Request-ID") ?? "",
  });
}

/**
 * apiGet이 모든 요청에 덧붙이는 mock 시나리오 파라미터.
 *
 * 요청 크기를 미리 재야 하는 쪽(최근 항목 청킹)이 같은 값을 봐야 하므로 여기서만
 * 읽습니다. 두 곳에서 각자 읽으면 한쪽만 바뀌어 계산이 조용히 어긋납니다.
 */
export function currentScenarioParam(): readonly (readonly [string, string])[] {
  const scenario = new URLSearchParams(window.location.search).get("scenario");
  return scenario ? [["scenario", scenario] as const] : [];
}

/**
 * 클러스터 범위 API 경로를 만듭니다.
 *
 * 같은 경로 템플릿이 화면·기능 모듈 곳곳에
 * 40번 가까이 반복되어 있었습니다. 반복 자체보다 나쁜 것은 **한 곳만 인코딩을 빠뜨려도
 * 아무도 모른다**는 점입니다 — 여기 하나로 모으면 모든 경로가 같은 규칙을 지납니다.
 * 세그먼트는 각각 인코딩되므로 `/`가 든 값을 넘겨도 경로가 쪼개지지 않습니다.
 */
export function clusterPath(clusterId: string, ...segments: (string | number)[]): string {
  let path = `/api/v1/clusters/${encodeURIComponent(clusterId)}`;
  for (const segment of segments) path += "/" + encodeURIComponent(String(segment));
  return path;
}

export type APIQueryParams = Record<string, string | readonly string[]> | readonly (readonly [string, string])[];

function queryEntries(params: APIQueryParams): readonly (readonly [string, string])[] {
  if (Array.isArray(params)) return params as readonly (readonly [string, string])[];
  return Object.entries(params as Record<string, string | readonly string[]>).flatMap(([key, value]) =>
      Array.isArray(value) ? value.map((item) => [key, item] as const) : [[key, value as string] as const]);
}

function appendQueryParams(url: URL, params: APIQueryParams) {
  const entries = queryEntries(params);
  for (const [key, value] of entries) url.searchParams.append(key, value);
}

async function getResponse(path: string, params: APIQueryParams, signal?: AbortSignal): Promise<Response> {
  const url = new URL(path, window.location.origin);
  appendQueryParams(url, params);
  for (const [key, value] of currentScenarioParam()) url.searchParams.set(key, value);
  return request(url.pathname + url.search, { signal });
}

export async function apiGet<T>(path: string, params: APIQueryParams = {}, signal?: AbortSignal): Promise<T> {
  const res = await getResponse(path, params, signal);
  if (!res.ok) return parseError(res);
  return (await res.json()) as T;
}

/** 새 wire는 본문을 읽기 전에 선택 확인 응답이 정확한지 검사합니다. */
export async function apiGetNamespaceSelected<T>(
  path: string,
  params: APIQueryParams,
  transport: NamespaceSelectionTransport,
  signal?: AbortSignal,
): Promise<T> {
  if (transport.mode === "blocked") throw new NamespaceSelectionAcknowledgementError();
  const base = queryEntries(params);
  const res = await getResponse(path, [...base, ...transport.params], signal);
  if (!res.ok) return parseError(res);
  if (transport.mode === "new" && !acknowledgementMatches(res.headers, transport.binding)) {
    try { await res.body?.cancel(); } catch { /* 확인 실패가 취소 오류로 바뀌면 안 됩니다. */ }
    throw new NamespaceSelectionAcknowledgementError();
  }
  return (await res.json()) as T;
}

export type APIRequestOptions = {
  /** Unsafe exact-target mutations can forbid the normal 401 refresh-and-resend path. */
  authRetry?: boolean;
};

export async function apiRequest<T>(path: string, init: RequestInit = {}, options: APIRequestOptions = {}): Promise<T> {
  const res = await request(path, init, options.authRetry !== false);
  if (!res.ok) return parseError(res);
  // 204·202(Accepted, 본문 없음)는 파싱하지 않습니다 — exec 세션 강제 종료는 202를 돌려줍니다.
  if (res.status === 204 || res.status === 202) return undefined as T;
  return (await res.json()) as T;
}

export async function apiDownload(path: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
  const res = await request(path, { signal });
  if (!res.ok) return parseError(res);
  if (!res.headers.get("content-type")?.startsWith("application/json")) throw new Error("Unexpected export content type");
  const match = /filename="([a-z][a-z0-9-]{0,63}\.json)"/.exec(res.headers.get("content-disposition") ?? "");
  return { blob: await res.blob(), filename: match?.[1] ?? "dashboard.json" };
}
